/**
 * Block 13 PR-G Phase 6.5G hotfix (2026-05-25) — SpecBlockEditor compact
 * tabular layout (Aibek visual verify round 6 fix #2).
 *
 * **Layout (mirror Block 12 `SectionComposition` pattern):**
 *
 *   1. Block header — «Мебель №N» pill + «удалить мебель» button (right).
 *   2. «Тип мебели» free-text input (e.g. «Кухня» / «Гардероб»).
 *   3. **Compact table** с column headers (Название flex-2 / Тип flex-1 /
 *      Бренд flex-1 / w-11 spacer для trash). 17 default custom rows из
 *      manual seed (либо КП import) рендерятся как thin `<li>` rows —
 *      `<SpecRowEditor row variant=custom/>`. Trash button per row.
 *   4. «+ Добавить позицию» dashed button — добавляет ещё одну custom row.
 *   5. **block_cost footer row** — full-width card с label «Стоимость» +
 *      cost input + currency adornment. Рендерится ПОД таблицей и кнопкой
 *      «+ Добавить позицию», поскольку block_cost — итог по блоку, не
 *      позиция.
 *
 * **Phase 6.5G hotfix round 3 (2026-05-25) — R3-#2:** Срок изготовления
 * read-only display удалён из header. Срок остаётся только в
 * SectionCostAndTerms (manual input). R3-#3: «Блок» → «Мебель» rename.
 *
 * **Previously (Phase 6.5F.2):** каждая позиция — expanded карточка с
 * separate Тип/Бренд блоками + cost input. Aibek screenshot
 * `Спецификация не правильная.png` зафиксировал визуальную проблему
 * (раздутые карточки на 17 позиций). Новый compact layout соответствует
 * Block 12 КП reference (`Спецификация.png`).
 *
 * **Testids preserved:**
 *   - `{prefix}` (root container)
 *   - `{prefix}-remove` (remove block button)
 *   - `{prefix}-title-input` (block title input)
 *   - `{prefix}-rows` (custom rows `<ul>` list)
 *   - `{prefix}-add-row` (+ Добавить позицию button)
 */

import { Plus, RotateCw, Trash2 } from 'lucide-react';
import type { ContractSpecBlock } from '@pomoshnik/shared';

import { genUuid } from '@/lib/uuid';

import { SpecRowEditor } from './SpecRowEditor';

const INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink font-semibold ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

// Phase 7 hotfix round 4 (R4-#1, 2026-05-26) — `touch-manipulation` устраняет
// 300ms tap delay + iOS Safari ghost-click (synthesized click после touchstart →
// natural click через ~300ms = двойной dispatch на мобильных). Aibek visual
// verify round 9 фиксировал «+ Добавить позицию добавляет 2 строки» — root cause
// hypothesis: ghost-click на iOS preview. Reducer + handler chain verified clean
// (single dispatch / single push) в unit tests; CSS fix предотвращает повторный
// synthetic click до того как он достигает React.
const DASHED_BUTTON_CLASS =
  'w-full min-h-tap-chip rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold ' +
  'flex items-center justify-center gap-2 touch-manipulation focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80';

export interface SpecBlockEditorProps {
  block: ContractSpecBlock;
  /** 1-based visual index of block (для labels). */
  index: number;
  currencyShort: string;
  /** Test-id prefix (e.g. `spec-block-{blockId}`). */
  testIdPrefix: string;
  onUpdateTitle: (value: string) => void;
  onRemoveBlock: () => void;
  /** Adds custom row (key='custom-<uuid>', removable=true). Caller передаёт rowId. */
  onAddCustomRow: (rowId: string) => void;
  onUpdateRowLabel: (rowId: string, value: string) => void;
  /** Тип separate field (A7 schema). */
  onUpdateRowType: (rowId: string, value: string | undefined) => void;
  /** Бренд separate field (A7 schema). */
  onUpdateRowBrand: (rowId: string, value: string | undefined) => void;
  onUpdateRowCost: (rowId: string, value: number | undefined) => void;
  onRemoveRow: (rowId: string) => void;
  /** PR-C5 (ADR-0039) — показать ссылку «↻ Обновить из КП» (только родной блок). */
  showReseedLink?: boolean;
  /** PR-C5 — клик «↻ Обновить из КП». */
  onReseedFromQuote?: () => void;
}

export function SpecBlockEditor({
  block,
  index,
  currencyShort,
  testIdPrefix,
  onUpdateTitle,
  onRemoveBlock,
  onAddCustomRow,
  onUpdateRowLabel,
  onUpdateRowType,
  onUpdateRowBrand,
  onUpdateRowCost,
  onRemoveRow,
  showReseedLink = false,
  onReseedFromQuote,
}: SpecBlockEditorProps) {
  // Phase 6.5G hotfix — separate block_cost row (footer) от custom rows (table).
  // Schema гарантирует ≤1 block_cost row per block (buildDefaultSpecRows seeds 1;
  // buildManualSpecRows тоже seeds 1; reducer ADD_SPEC_BLOCK не создаёт дубликаты).
  const blockCostRow = block.rows.find((r) => r.key === 'block_cost');
  const customRows = block.rows.filter((r) => r.key !== 'block_cost');

  return (
    <div
      data-testid={testIdPrefix}
      className="rounded-xl border-[1.5px] border-accent bg-card p-3 mb-3 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-ink uppercase tracking-wide">Мебель №{index}</span>
        <button
          type="button"
          onClick={onRemoveBlock}
          aria-label={`Удалить мебель №${index}`}
          data-testid={`${testIdPrefix}-remove`}
          className="flex items-center gap-1 text-destructive text-sm font-medium px-2 py-1 rounded-md focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
        >
          <Trash2 size={16} aria-hidden="true" />
          удалить мебель
        </button>
      </div>

      {/* PR-C5 (ADR-0039) — ссылка «↻ Обновить из КП» в родном блоке, видна
          когда КП новее последнего сева. Клик → пересев родного блока (= «Да»). */}
      {showReseedLink && onReseedFromQuote ? (
        <button
          type="button"
          onClick={onReseedFromQuote}
          aria-label="Обновить данные блока из КП"
          data-testid={`${testIdPrefix}-reseed-link`}
          className="self-start flex items-center gap-1.5 text-sm font-semibold text-ink underline underline-offset-2 px-1 py-1 rounded-md focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70 touch-manipulation"
        >
          <RotateCw size={15} aria-hidden="true" />
          Обновить из КП
        </button>
      ) : null}

      <div>
        <label
          htmlFor={`${testIdPrefix}-title-input`}
          className="block text-sm font-semibold text-ink/80 mb-1"
        >
          Тип мебели
        </label>
        <input
          id={`${testIdPrefix}-title-input`}
          type="text"
          value={block.title}
          onChange={(e) => onUpdateTitle(e.target.value)}
          aria-label={`Название мебели №${index}`}
          data-testid={`${testIdPrefix}-title-input`}
          placeholder="Например: Кухня"
          className={INPUT_CLASS}
        />
      </div>

      {/* Compact table: column header row + thin <li> body rows. Mirror Block
          12 SectionComposition (`apps/web/src/features/calculator/quote/sections/
          SectionComposition.tsx`). */}
      <div className="flex flex-col">
        <div
          role="row"
          aria-hidden="true"
          className="flex items-center gap-1.5 pb-2 mb-1 border-b-[1.5px] border-accent text-xs uppercase tracking-wider font-bold text-ink/60"
        >
          <div className="flex-[2_1_0] min-w-0">Название</div>
          <div className="flex-[1_1_0] min-w-0">Тип</div>
          <div className="flex-[1_1_0] min-w-0">Бренд</div>
          <div className="w-11 flex-shrink-0" aria-hidden="true" />
        </div>

        <ul role="list" data-testid={`${testIdPrefix}-rows`} className="flex flex-col m-0 p-0">
          {customRows.map((row, rowIdx) => (
            <SpecRowEditor
              key={row.id}
              row={row}
              index={rowIdx + 1}
              currencyShort={currencyShort}
              testIdPrefix={`${testIdPrefix}-row-${row.id}`}
              onUpdateLabel={(value) => onUpdateRowLabel(row.id, value)}
              onUpdateType={(value) => onUpdateRowType(row.id, value)}
              onUpdateBrand={(value) => onUpdateRowBrand(row.id, value)}
              onUpdateCost={(value) => onUpdateRowCost(row.id, value)}
              onRemove={() => onRemoveRow(row.id)}
            />
          ))}
        </ul>
      </div>

      <button
        type="button"
        onClick={() => onAddCustomRow(genUuid())}
        aria-label={`Добавить позицию в мебель №${index}`}
        data-testid={`${testIdPrefix}-add-row`}
        className={DASHED_BUTTON_CLASS}
      >
        <Plus size={18} aria-hidden="true" />+ Добавить позицию
      </button>

      {/* block_cost footer row — итог по блоку, не позиция. Рендерится ПОД
          таблицей и «+ Добавить позицию». Schema invariant: ≤1 block_cost
          row per block. */}
      {blockCostRow ? (
        <SpecRowEditor
          row={blockCostRow}
          index={0}
          currencyShort={currencyShort}
          testIdPrefix={`${testIdPrefix}-row-${blockCostRow.id}`}
          onUpdateLabel={(value) => onUpdateRowLabel(blockCostRow.id, value)}
          onUpdateType={(value) => onUpdateRowType(blockCostRow.id, value)}
          onUpdateBrand={(value) => onUpdateRowBrand(blockCostRow.id, value)}
          onUpdateCost={(value) => onUpdateRowCost(blockCostRow.id, value)}
          onRemove={() => onRemoveRow(blockCostRow.id)}
        />
      ) : null}
    </div>
  );
}
